import React, { useState, useCallback, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Chip,
} from '@mui/material'
import { Close, Delete, Add } from '@mui/icons-material'
import axios from 'src/utilities/http-client'

interface VocabularyItem {
  phrase: string
  soundsLike?: string
  ipa?: string
  displayAs?: string
}

interface VocabularyResponse {
  vocabularyName: string
  vocabularyState: string
  terms: VocabularyItem[]
}

interface VocabularyManagementModalProps {
  open: boolean
  onClose: () => void
  onAddVocabulary: (word?: string) => void
}

export function VocabularyManagementModal({
  open,
  onClose,
  onAddVocabulary,
}: VocabularyManagementModalProps) {
  let [vocabularyItems, setVocabularyItems] = useState<VocabularyItem[]>([])
  let [isLoading, setIsLoading] = useState(false)
  let [error, setError] = useState<string | null>(null)
  let [deletingItems, setDeletingItems] = useState<Set<string>>(new Set())

  let loadVocabulary = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      let response = await axios.get('/api/create/video/vocabulary')
      let vocabData = response.data as VocabularyResponse
      setVocabularyItems(vocabData.terms || [])
    } catch (e) {
      let errorMessage =
        e instanceof Error ? e.message : 'Failed to load vocabulary'
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }, [])

  let handleDeleteVocabulary = useCallback(
    async (phrase: string) => {
      setDeletingItems((prev) => new Set([...prev, phrase]))

      try {
        await axios.delete(
          `/api/create/video/vocabulary?phrase=${encodeURIComponent(phrase)}`,
        )

        // Refresh the vocabulary list
        await loadVocabulary()
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to delete vocabulary item:', e)
        setError('Failed to delete vocabulary item')
      } finally {
        setDeletingItems((prev) => {
          let newSet = new Set(prev)
          newSet.delete(phrase)
          return newSet
        })
      }
    },
    [loadVocabulary],
  )

  // Load vocabulary when modal opens
  useEffect(() => {
    if (open) {
      loadVocabulary()
    }
  }, [open, loadVocabulary])

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6">Manage Vocabulary</Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Manage custom vocabulary to improve transcription accuracy for
          technical or medical terms.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => onAddVocabulary()}
          >
            Add New Term
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : vocabularyItems.length === 0 ? (
          <Alert severity="info">
            No vocabulary terms added yet. Add your first term to improve
            transcription accuracy.
          </Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Phrase</TableCell>
                  <TableCell>Sounds Like</TableCell>
                  <TableCell>IPA</TableCell>
                  <TableCell>Display As</TableCell>
                  <TableCell width={100}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vocabularyItems.map((item, index) => (
                  <TableRow key={`${item.phrase}-${index}`}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {item.phrase}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {item.soundsLike ? (
                        <Chip
                          label={item.soundsLike}
                          size="small"
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {item.ipa ? (
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: 'monospace' }}
                        >
                          {item.ipa}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {item.displayAs ? (
                        <Typography variant="body2">
                          {item.displayAs}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Delete vocabulary term">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteVocabulary(item.phrase)}
                          disabled={deletingItems.has(item.phrase)}
                          color="error"
                        >
                          {deletingItems.has(item.phrase) ? (
                            <CircularProgress size={16} />
                          ) : (
                            <Delete />
                          )}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
